import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { api } from '../../shared/api/apiClient';
import type {
    OperationDefinitionDto,
    OperationRunDetailsDto,
    OperationRunDto,
    OperationScheduleDto,
    OperationScheduleSaveRequest
} from '../../types';
import './styles.css';
import SlideInPanel from './SlideInPanel';
import OperationsDefinitionList from './OperationsDefinitionList';
import OperationDetailPanel from './OperationDetailPanel';
import OperationRunLogPanel from './OperationRunLogPanel';
import OperationsJournalPanel from './OperationsJournalPanel';
import {
    buildDefaultParametersJson,
    formatDateTime,
    formatDuration,
    parseRefreshInterval,
    statusLabel,
    triggerLabel
} from './utils.tsx';

const OperationsManagement: React.FC = () => {
    const [definitions, setDefinitions] = useState<OperationDefinitionDto[]>([]);
    const [runs, setRuns] = useState<OperationRunDto[]>([]);
    const [selectedDefinition, setSelectedDefinition] = useState<OperationDefinitionDto | null>(null);
    const [selectedSchedule, setSelectedSchedule] = useState<OperationScheduleDto | null>(null);
    const [selectedRun, setSelectedRun] = useState<OperationRunDetailsDto | null>(null);
    const [selectedRunId, setSelectedRunId] = useState<number | null>(null);
    const [selectedOpRuns, setSelectedOpRuns] = useState<OperationRunDto[]>([]);
    const [isFullJournalOpen, setIsFullJournalOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [runLoading, setRunLoading] = useState<string | null>(null);
    const [scheduleLoading, setScheduleLoading] = useState(false);
    const [editCron, setEditCron] = useState('');
    const [editTimezone, setEditTimezone] = useState('UTC');
    const [editParams, setEditParams] = useState('{}');
    const [refreshInterval, setRefreshInterval] = useState<number>(() => parseRefreshInterval(localStorage.getItem('operationsRefreshInterval')));
    const [journalFilterOp, setJournalFilterOp] = useState('');
    const [journalFilterStatus, setJournalFilterStatus] = useState('');
    const [journalRuns, setJournalRuns] = useState<OperationRunDto[]>([]);
    const [searchParams, setSearchParams] = useSearchParams();
    const opCode = searchParams.get('op');

    const loadDefinitions = useCallback(async () => {
        const response = await api.get<OperationDefinitionDto[]>('/api/operations/definitions');
        setDefinitions(response.data);
    }, []);

    const loadRuns = useCallback(async (limit = 20) => {
        const response = await api.get<OperationRunDto[]>(`/api/operations/runs?limit=${limit}`);
        setRuns(response.data);
        return response.data;
    }, []);

    const loadOperationRuns = useCallback(async (operationCode: string, limit = 10) => {
        const response = await api.get<OperationRunDto[]>(`/api/operations/runs?operationCode=${operationCode}&limit=${limit}`);
        setSelectedOpRuns(response.data);
    }, []);

    const loadSchedule = useCallback(async (operationCode: string, defaultParametersJson = '{}') => {
        try {
            const response = await api.get<OperationScheduleDto>(`/api/operations/${operationCode}/schedule`);
            setSelectedSchedule(response.data);
            setEditCron(response.data.cron ?? '');
            setEditTimezone(response.data.timeZoneId ?? 'UTC');
            setEditParams(response.data.parametersJson || defaultParametersJson);
        } catch {
            setSelectedSchedule(null);
            setEditCron('');
            setEditTimezone('UTC');
            setEditParams(defaultParametersJson);
        }
    }, []);

    const loadRunDetails = useCallback(async (runId: number) => {
        const response = await api.get<OperationRunDetailsDto>(`/api/operations/runs/${runId}`);
        setSelectedRun(response.data);
        setSelectedRunId(runId);
    }, []);

    const loadJournal = useCallback(async () => {
        const params = new URLSearchParams();
        params.set('limit', '50');
        if (journalFilterOp) params.set('operationCode', journalFilterOp);
        if (journalFilterStatus) params.set('status', journalFilterStatus);
        const response = await api.get<OperationRunDto[]>(`/api/operations/runs?${params.toString()}`);
        setJournalRuns(response.data);
    }, [journalFilterOp, journalFilterStatus]);

    const loadInitialData = useCallback(async () => {
        setLoading(true);
        try {
            await Promise.all([loadDefinitions(), loadRuns()]);
        } finally {
            setLoading(false);
        }
    }, [loadDefinitions, loadRuns]);

    useEffect(() => {
        void loadInitialData();
    }, [loadInitialData]);

    useEffect(() => {
        const op = searchParams.get('op');
        const run = searchParams.get('run');
        const journal = searchParams.get('journal');

        if (journal === '1') {
            void loadJournal();
            setIsFullJournalOpen(true);
        }

        if (op && definitions.length > 0) {
            const def = definitions.find(d => d.code === op);
            if (def && !selectedDefinition) {
                setSelectedDefinition(def);
                const defaultParams = buildDefaultParametersJson(def);
                setEditParams(defaultParams);
                if (def.supportsSchedule) {
                    void loadSchedule(def.code, defaultParams);
                } else {
                    setSelectedSchedule(null);
                    setEditCron('');
                    setEditTimezone('UTC');
                }
            }
        }

        if (run && !selectedRun) {
            const runId = parseInt(run, 10);
            if (!Number.isNaN(runId)) {
                void loadRunDetails(runId);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [definitions.length > 0]);

    const handleSelectOperation = async (definition: OperationDefinitionDto) => {
        setSearchParams({ op: definition.code });
        setSelectedDefinition(definition);
        setSelectedRun(null);
        setSelectedRunId(null);
        const defaultParametersJson = buildDefaultParametersJson(definition);
        setEditParams(defaultParametersJson);

        if (definition.supportsSchedule) {
            await loadSchedule(definition.code, defaultParametersJson);
        } else {
            setSelectedSchedule(null);
            setEditCron('');
            setEditTimezone('UTC');
        }
    };

    const handleRun = async () => {
        if (!selectedDefinition) return;

        const confirmed = !selectedDefinition.requiresConfirmation || window.confirm(`Запустить операцию «${selectedDefinition.title}»?`);
        if (!confirmed) return;

        setRunLoading(selectedDefinition.code);
        try {
            const response = await api.post<OperationRunDto>(
                `/api/operations/${selectedDefinition.code}/runs`,
                { parametersJson: JSON.stringify(JSON.parse(editParams || '{}')) }
            );
            toast.success('Операция поставлена в очередь.');
            await Promise.all([loadRuns(), loadOperationRuns(selectedDefinition.code), loadRunDetails(response.data.id)]);
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Ошибка запуска';
            toast.error(message);
        } finally {
            setRunLoading(null);
        }
    };

    const handleSaveSchedule = async () => {
        if (!selectedDefinition) return;

        setScheduleLoading(true);
        try {
            const body: OperationScheduleSaveRequest = {
                cron: editCron,
                timeZoneId: editTimezone,
                parametersJson: editParams
            };
            await api.put(`/api/operations/${selectedDefinition.code}/schedule`, body);
            toast.success('Расписание сохранено.');
            await loadSchedule(selectedDefinition.code);
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Ошибка сохранения расписания';
            toast.error(message);
        } finally {
            setScheduleLoading(false);
        }
    };

    const handleDeleteSchedule = async () => {
        if (!selectedDefinition || !selectedSchedule) return;

        if (!window.confirm('Удалить расписание?')) return;

        setScheduleLoading(true);
        try {
            await api.delete(`/api/operations/${selectedDefinition.code}/schedule`);
            toast.success('Расписание удалено.');
            setSelectedSchedule(null);
            setEditCron('');
            setEditTimezone('UTC');
            setEditParams('{}');
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Ошибка удаления расписания';
            toast.error(message);
        } finally {
            setScheduleLoading(false);
        }
    };

    const handleRefreshRuns = useCallback(async () => {
        await loadRuns();
    }, [loadRuns]);

    const handleFullRefresh = useCallback(async () => {
        await loadRuns();
        if (selectedRunId) {
            await loadRunDetails(selectedRunId);
        }
    }, [loadRunDetails, loadRuns, selectedRunId]);

    const handleOpenRunLog = async (runId?: number) => {
        const loadedRuns = await loadRuns();
        const runIdToSelect = runId
            ?? (selectedRunId && loadedRuns.some(run => run.id === selectedRunId) ? selectedRunId : loadedRuns[0]?.id);

        if (runIdToSelect) {
            setSearchParams(prev => {
                const params = new URLSearchParams(prev);
                params.set('run', runIdToSelect.toString());
                return params;
            });
            await loadRunDetails(runIdToSelect);
        }
    };

    const handleOpenJournal = async () => {
        setSearchParams(prev => {
            const params = new URLSearchParams(prev);
            params.set('journal', '1');
            return params;
        });
        await loadJournal();
        setIsFullJournalOpen(true);
    };

    const handleCloseOperationDetail = useCallback(() => {
        setSearchParams({});
        setSelectedDefinition(null);
        setSelectedRun(null);
        setSelectedRunId(null);
        setSelectedOpRuns([]);
    }, [setSearchParams]);

    const handleCloseRunLog = useCallback(() => {
        setSearchParams(prev => {
            const params = new URLSearchParams(prev);
            params.delete('run');
            return params;
        });
        setSelectedRun(null);
        setSelectedRunId(null);
    }, [setSearchParams]);

    const handleCloseJournal = useCallback(() => {
        setSearchParams(prev => {
            const params = new URLSearchParams(prev);
            params.delete('journal');
            return params;
        });
        setIsFullJournalOpen(false);
        setJournalRuns([]);
    }, [setSearchParams]);

    const handleBreadcrumbHome = useCallback(() => {
        setSearchParams({});
        setSelectedDefinition(null);
        setSelectedRun(null);
        setSelectedRunId(null);
        setSelectedOpRuns([]);
    }, [setSearchParams]);

    const handleBreadcrumbOperation = useCallback(() => {
        setSearchParams(opCode ? { op: opCode } : {});
        setSelectedRun(null);
        setSelectedRunId(null);
    }, [setSearchParams, opCode]);

    useEffect(() => {
        localStorage.setItem('operationsRefreshInterval', refreshInterval.toString());
    }, [refreshInterval]);

    useEffect(() => {
        if (selectedDefinition) {
            setSelectedOpRuns(runs.filter(r => r.operationCode === selectedDefinition.code));
        }
    }, [runs, selectedDefinition]);

    useEffect(() => {
        if (selectedRun && runs.length > 0) {
            const matchingRun = runs.find(r => r.id === selectedRun.id);
            if (matchingRun && matchingRun.status !== selectedRun.status) {
                void loadRunDetails(selectedRun.id);
            }
        }
    }, [runs, selectedRun, loadRunDetails]);

    useEffect(() => {
        if (refreshInterval <= 0) return;

        const intervalId = window.setInterval(() => {
            void handleRefreshRuns();
        }, refreshInterval * 1000);

        return () => window.clearInterval(intervalId);
    }, [handleRefreshRuns, refreshInterval]);

    const latestRun = runs[0] ?? null;

    if (loading) {
        return (
            <div className="operations-page">
                <div className="spinner-border" role="status">
                    <span className="visually-hidden">Загрузка...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="operations-page">
            <div className="operations-header">
                <div>
                    <h1>Операции</h1>
                    <p>Запуск и планирование операций над данными.</p>
                </div>
                <div className="operations-header-controls">
                    {latestRun && (
                        <div className="operations-last-run">
                            <span>Последний запуск</span>
                            <strong>#{latestRun.id}</strong>
                            <span className={`operations-status status-${latestRun.status.toLowerCase()}`}>
                                {statusLabel[latestRun.status] ?? latestRun.status}
                            </span>
                        </div>
                    )}
                    <button
                        className="btn btn-outline-secondary"
                        disabled={runs.length === 0}
                        onClick={() => void handleOpenRunLog(latestRun?.id)}
                    >
                        <i className="bi bi-journal-text"></i>
                        Детали запуска
                    </button>
                    <button
                        className="btn btn-outline-secondary"
                        onClick={handleOpenJournal}
                    >
                        <i className="bi bi-journal-text"></i>
                        Полный журнал
                    </button>
                </div>
            </div>

            <div className="operations-layout operations-layout-single">
                <div className="operations-list">
                    <OperationsDefinitionList
                        definitions={definitions}
                        selectedDefinition={selectedDefinition}
                        onSelectOperation={handleSelectOperation}
                    />

                    <section className="operations-section operations-recent-section">
                        <h2>Последние запуски</h2>
                        {runs.length > 0 ? (
                            <div className="operations-table-wrap">
                                <table className="table table-hover">
                                    <thead>
                                        <tr>
                                            <th>ID</th>
                                            <th>Операция</th>
                                            <th>Статус</th>
                                            <th>Триггер</th>
                                            <th>Запущено</th>
                                            <th>Длительность</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {runs.map(run => (
                                            <tr
                                                key={run.id}
                                                className={selectedRunId === run.id ? 'table-active' : ''}
                                                onClick={() => void handleOpenRunLog(run.id)}
                                            >
                                                <td>{run.id}</td>
                                                <td>{run.operationTitle}</td>
                                                <td>
                                                    <span className={`operations-status status-${run.status.toLowerCase()}`}>
                                                        {statusLabel[run.status] ?? run.status}
                                                    </span>
                                                </td>
                                                <td>{triggerLabel[run.trigger] ?? run.trigger}</td>
                                                <td>{formatDateTime(run.createdAt)}</td>
                                                <td>{formatDuration(run)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="operations-empty">Запусков пока нет.</div>
                        )}
                    </section>
                </div>
            </div>

            <SlideInPanel
                isOpen={!!selectedDefinition && !selectedRun}
                onClose={handleCloseOperationDetail}
                width="50%"
                breadcrumbs={[
                    { label: 'Операции', onClick: handleBreadcrumbHome },
                    ...(selectedDefinition ? [{ label: selectedDefinition.title, onClick: handleBreadcrumbOperation }] : []),
                ]}
            >
                <OperationDetailPanel
                    selectedDefinition={selectedDefinition}
                    selectedSchedule={selectedSchedule}
                    selectedOpRuns={selectedOpRuns}
                    selectedRunId={selectedRunId}
                    editParams={editParams}
                    runLoading={runLoading}
                    scheduleLoading={scheduleLoading}
                    editCron={editCron}
                    editTimezone={editTimezone}
                    onEditParamsChange={setEditParams}
                    onEditCronChange={setEditCron}
                    onEditTimezoneChange={setEditTimezone}
                    onRun={handleRun}
                    onSaveSchedule={handleSaveSchedule}
                    onDeleteSchedule={handleDeleteSchedule}
                    onOpenRunLog={runId => void handleOpenRunLog(runId)}
                />
            </SlideInPanel>

            <SlideInPanel
                isOpen={!!selectedRun}
                onClose={handleCloseRunLog}
                width="70%"
                level={2}
                breadcrumbs={[
                    { label: 'Операции', onClick: handleBreadcrumbHome },
                    ...(selectedDefinition
                        ? [{ label: selectedDefinition.title, onClick: handleBreadcrumbOperation }]
                        : []),
                    ...(selectedRun ? [{ label: `#${selectedRun.id}`, onClick: () => {} }] : []),
                ]}
            >
                <OperationRunLogPanel selectedRun={selectedRun} />
            </SlideInPanel>

            <SlideInPanel
                isOpen={isFullJournalOpen}
                onClose={handleCloseJournal}
                width="80%"
                level={2}
                breadcrumbs={[
                    { label: 'Операции', onClick: handleBreadcrumbHome },
                    { label: 'Полный журнал', onClick: () => {} },
                ]}
            >
                <OperationsJournalPanel
                    definitions={definitions}
                    journalRuns={journalRuns}
                    journalFilterOp={journalFilterOp}
                    journalFilterStatus={journalFilterStatus}
                    setJournalFilterOp={setJournalFilterOp}
                    setJournalFilterStatus={setJournalFilterStatus}
                    onApplyFilters={loadJournal}
                    onSelectJournalRun={async runId => {
                        await loadRunDetails(runId);
                        setIsFullJournalOpen(false);
                    }}
                />
            </SlideInPanel>

            <div className="operations-refresh-controls">
                <label className="operations-refresh-control">
                    <span>Обновлять запуски</span>
                    <select
                        value={refreshInterval}
                        onChange={event => setRefreshInterval(parseInt(event.target.value, 10))}
                    >
                        <option value={0}>не нужно</option>
                        <option value={3}>каждые 3 сек</option>
                        <option value={5}>каждые 5 сек</option>
                        <option value={10}>каждые 10 сек</option>
                        <option value={30}>каждые 30 сек</option>
                    </select>
                </label>
                <button className="btn btn-outline-secondary" onClick={handleFullRefresh}>
                    <i className="bi bi-arrow-clockwise"></i>
                    Обновить
                </button>
            </div>
        </div>
    );
};

export default OperationsManagement;
